import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "@chakra-ui/react";
import noop from "lodash/noop";
import { FullBleedSection, MaxContentSection } from "~/components";
import { Trans } from "~/utils/i18next";
import { trackPlanSelect as track } from "../../../events";
import { useGetMembershipSelectAction } from "../hooks";
import { DividerSection } from "./DividerSection";
import { Headings } from "./Headings";
import { MobileCtas } from "./MobileCtas";
import { PlanBoxes } from "./PlanBoxes";
import { PlansTable } from "./PlansTable";
import { ScheduledIntervalChangeBanner } from "./ScheduledIntervalChangeBanner";
import { TrialDiscountBanner } from "./TrialDiscountBanner";

const premiumTrackerByAction = {
  extend: track.extendToPremium,
  payment: track.premiumMembershipPlan,
  resubscribe: track.resubscribeToPremium,
  upgrade: track.upgradeToPremium,
};

const plusTrackerByAction = {
  extend: track.extendToPlus,
  payment: track.plusMembershipPlan,
  resubscribe: track.resubscribeToPlus,
  upgrade: noop,
};

export const Body: FC = () => {
  const action = useGetMembershipSelectAction();
  const { t } = useTranslation();

  const premiumTracker = premiumTrackerByAction[action];
  const plusTracker = plusTrackerByAction[action];

  const base = `planSelect.${action}` as const;
  const subtitle = t(`${base}.subtitle`) ? (
    <Trans i18nKey={`${base}.subtitle`} />
  ) : null;

  return (
    <FullBleedSection bgColor="bg-main" overflow="hidden">
      <MaxContentSection
        alignItems="center"
        h="full"
        justify="flex-start"
        overflow="hidden"
        pt={{ base: 8, md: 14 }}
        pb={{ base: 8, md: 2 }}
        w="full"
      >
        <Headings
          heading={<Trans i18nKey={`${base}.title`} />}
          subtitle={subtitle}
        />
      </MaxContentSection>
      <Flex direction="column" my={{ base: 2, md: 5 }} w="full">
        <ScheduledIntervalChangeBanner />
        <TrialDiscountBanner />
      </Flex>
      <MaxContentSection
        alignItems="center"
        gap={{ base: "3.5rem", md: "6rem" }}
        h="full"
        justify="flex-start"
        pb="8rem"
        w="full"
      >
        <PlanBoxes action={action} trackers={{ plusTracker, premiumTracker }} />
        <Flex direction="column" gap={8}>
          <DividerSection />
          <Flex direction="column" gap="5rem" mb="4rem">
            <PlansTable
              action={action}
              trackers={{ plusTracker, premiumTracker }}
            />
            <MobileCtas
              action={action}
              trackers={{ plusTracker, premiumTracker }}
            />
          </Flex>
        </Flex>
      </MaxContentSection>
    </FullBleedSection>
  );
};
